import React, {useEffect, useMemo} from 'react';
import {z} from 'zod';
import {clsx} from 'clsx';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useValidationErrors} from '@/Hooks/useValidationErrors';
import {GtaRate, GtaRateStatusEnum} from "@/Stub/V1";
import {snakeToNormalCase} from "@/Helpers/general";
import {Button} from "primereact/button";
import BaseDropdown from "@/Components/Forms/Form/BaseDropdown";
import BaseTextInput from "@/Components/Forms/Form/BaseTextInput";
import BaseNumberInput from "@/Components/Forms/Form/BaseNumberInput";

const GtaRateFormSchema = z.object({
    gta_group: z.string({
        required_error: 'GTA Group is required'
    }),
    description: z.string().optional(),
    status: z.nativeEnum(GtaRateStatusEnum, {
        required_error: 'Status is required'
    }),
    daily_hire_rate: z.number({
        required_error: 'GTA Rate is required'
    }).min(0).positive(),
    gta_sort_index: z.number({
        required_error: 'Sort Index is required',
    }).min(1).positive().int()

});

export type GtaRateFormData = z.infer<typeof GtaRateFormSchema>;

const statusOptions = Object.entries(GtaRateStatusEnum).map(([key, value]) => ({
    key,
    value,
    label: snakeToNormalCase(value),
}));

export type GtaRateFormProps = {
    className?: string
    onSubmit?: (data: GtaRateFormData) => void
    onSubmitButtonLabel?: string
    onSubmitButtonDisabled?: boolean
    isProcessing?: boolean
    initFormData?: Partial<GtaRate>
    errorBag?: Error[]
};

const GtaRateForm: React.FC<GtaRateFormProps> = ({
                                                     className,
                                                     onSubmit,
                                                     isProcessing,
                                                     initFormData,
                                                     onSubmitButtonLabel,
                                                     onSubmitButtonDisabled = false,
                                                     errorBag
                                                 }: GtaRateFormProps) => {
    const formInitialValues = useMemo(() => {
        if (initFormData) {
            return initFormData
        }

        return {
            gta_group: '',
            description: '',
            status: GtaRateStatusEnum.Active,
            daily_hire_rate: 0,
            gta_sort_index: 1.22,
        };
    }, [initFormData]);

    const {
        control,
        setError,
        reset,
        formState: {errors},
        handleSubmit,
    } = useForm<GtaRateFormData>({
        resolver: zodResolver(GtaRateFormSchema),
        defaultValues: formInitialValues
    });

    const onFormSubmit = (data: GtaRateFormData) => {
        if (onSubmit) {
            onSubmit(data);
        }
    };

    useEffect(() => {
        reset(formInitialValues);
    }, [initFormData, reset]);

    useValidationErrors(setError, errorBag);

    return (
        <div className={clsx(className, 'gta-rate-form')}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <div className="grid">
                    <h4 className={'col-12 my-0 font-bold'}>Create GTA Group</h4>
                    <BaseTextInput
                        className={"col-6 rounded-md"}
                        pt={{inputText: {className: 'w-full'}}}
                        control={control}
                        name={"gta_group"}
                        label={'GTA Group'}
                        errorMessages={errors.gta_group?.message}
                    />
                    <BaseDropdown
                        className={"col-6 rounded-md"}
                        pt={{dropdown: {className: 'w-full'}}}
                        control={control}
                        options={statusOptions}
                        required
                        name={"status"}
                        label={'Status'}
                        errorMessages={errors.status?.message}
                    />
                    <BaseTextInput
                        className={"col-12 rounded-md"}
                        pt={{inputText: {className: 'w-full'}}}
                        control={control}
                        name={"description"}
                        label={'Description'}
                        errorMessages={errors.description?.message}
                    />
                    <BaseNumberInput
                        className={"col-6 rounded-md"}
                        control={control}
                        name={"daily_hire_rate"}
                        label={'Daily Hire Rate'}
                        errorMessages={errors.daily_hire_rate?.message}
                        mode="currency"
                        currency={'GBP'}
                    />
                    <BaseNumberInput
                        className={"col-6 rounded-md"}
                        control={control}
                        name={"gta_sort_index"}
                        label={'Sort Index'}
                        errorMessages={errors.gta_sort_index?.message}
                        maxFractionDigits={0}
                        min={1}
                        step={1}
                        showButtons
                    />
                </div>
                <div className="flex mt-4">
                    <Button
                        className={"ml-auto"}
                        label={onSubmitButtonLabel}
                        disabled={onSubmitButtonDisabled}
                        type={"submit"}
                        severity={"success"}
                        loading={isProcessing}
                    />
                </div>
            </form>
        </div>
    );
};

export default GtaRateForm;
